import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { SocialLinks } from '@/components/layout/SocialLinks';
import { socialLinks } from '@/components/layout/social';
import { MapFrame } from '@/components/map/MapFrame';
import { WhatsAppButton } from '@/components/product/WhatsAppButton';
import { Placeholder } from '@/components/ui/Placeholder';
import { usePublicSettings } from '@/lib/site/public-settings';
import { httpsOnly } from '@/lib/site/safe-url';
import { content } from './content';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="contact-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * How to reach and find the firm — address (with its own line breaks), phone, e-mail, WhatsApp, the social
 * networks as logos and a map — all from the public site settings. A fact the owner has cleared is shown as a
 * visibly marked `{{PLACEHOLDER}}` (see docs/CONTENT_TODO.md), never as an invented value; opening hours are
 * not a section of this page unless someone sets them (D-318).
 */
export function ContactDetails() {
  const copy = content.details;
  const settings = usePublicSettings();
  const phoneHref = settings.phone ? `tel:${settings.phone.replace(/[^\d+]/g, '')}` : null;
  const map = httpsOnly(settings.mapEmbedUrl);
  const socials = socialLinks(settings.social);

  return (
    <div className="contact-details">
      <h2 className="t-h3">{copy.title}</h2>
      <dl className="contact-facts">
        <Fact label={copy.address}>
          {settings.address ? (
            <span className="contact-fact__multiline">{settings.address}</span>
          ) : (
            <Placeholder token="ADDRESS" />
          )}
        </Fact>
        {settings.hours ? (
          <Fact label={copy.hours}>
            <span className="contact-fact__multiline">{settings.hours}</span>
          </Fact>
        ) : null}
        <Fact label={copy.phone}>
          {phoneHref && settings.phone ? (
            <a href={phoneHref}>{settings.phone}</a>
          ) : (
            <Placeholder token="PHONE" />
          )}
        </Fact>
        <Fact label={copy.email}>
          {settings.email ? (
            <a href={`mailto:${settings.email}`}>{settings.email}</a>
          ) : (
            <Placeholder token="CONTACT_EMAIL" />
          )}
        </Fact>
        <Fact label={copy.whatsapp}>
          <WhatsAppButton message={copy.whatsappMessage} />
          <span className="sr-only">{copy.chat}</span>
        </Fact>
        {socials.length > 0 ? (
          <Fact label={copy.follow}>
            <SocialLinks links={socials} />
          </Fact>
        ) : null}
      </dl>

      <div className="contact-why">
        <h3>{copy.reasonsTitle}</h3>
        <ul>
          {copy.reasons.map((reason) => (
            <li key={reason}>
              <Check size={16} aria-hidden="true" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      </div>

      <MapFrame embedUrl={map} address={settings.address} />
    </div>
  );
}
